import React, { useCallback, useEffect, useRef } from "react";
import { MessageAnimationContext } from "../messageAnimations";
import { AudioPlaybackHandle } from "../../../../core/engine/types/audio-manager";

type KitKitProps = MessageAnimationContext & {
  analysisTrackUrl: string;
  fraggerName: string;
};

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  alpha: number;
  size: number;
  color: string;
}

interface TextBurst {
  x: number;
  y: number;
  text: string;
  life: number;
  alpha: number;
  scale: number;
  color: string;
}

const CAT_COLORS = ["#FFD700", "#50C878", "#DA70D6", "#00D4FF"];
const TEXT_WORDS = ["KIT", "MIAU"];

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

const createParticle = (width: number, height: number): Particle => ({
  x: Math.random() * width,
  y: height + 40,
  vx: (Math.random() - 0.5) * 1.2,
  vy: -(Math.random() * 2 + 0.8),
  life: 1,
  alpha: 0,
  size: Math.random() * 2 + 1,
  color: CAT_COLORS[Math.floor(Math.random() * CAT_COLORS.length)],
});

const createTextBurst = (
  width: number,
  height: number,
  text: string
): TextBurst => ({
  x: width / 2 + (Math.random() - 0.5) * width * 0.4,
  y: height / 2 + (Math.random() - 0.5) * height * 0.4,
  text,
  life: 1,
  alpha: 1,
  scale: 0.5,
  color: text === "KIT" ? "#FFD700" : "#DA70D6",
});

const drawCatOutline = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  color: string,
  lineWidth: number,
  rotation: number
) => {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rotation);
  ctx.scale(size / 100, size / 100);
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth / (size / 100);
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  ctx.beginPath();
  ctx.moveTo(-30, 10);
  ctx.quadraticCurveTo(-45, 0, -35, -25);
  ctx.lineTo(-45, -50);
  ctx.lineTo(-15, -35);
  ctx.quadraticCurveTo(0, -45, 15, -35);
  ctx.lineTo(45, -50);
  ctx.lineTo(35, -25);
  ctx.quadraticCurveTo(45, 0, 30, 10);
  ctx.quadraticCurveTo(0, 35, -30, 10);
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(-5, 5);
  ctx.lineTo(-45, 5);
  ctx.moveTo(-5, 15);
  ctx.lineTo(-45, 20);
  ctx.moveTo(5, 5);
  ctx.lineTo(45, 5);
  ctx.moveTo(5, 15);
  ctx.lineTo(45, 20);
  ctx.stroke();

  ctx.restore();
};

const KitKitMiauMiauAnimation: React.FC<KitKitProps> = ({
  analysisTrackUrl,
  fraggerName,
  ...context
}) => {
  const { message, playAudio, onComplete } = context;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const freqDataRef = useRef<Uint8Array | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analysisAudioRef = useRef<HTMLAudioElement | null>(null);
  const analyserSourceRef = useRef<
    MediaStreamAudioSourceNode | MediaElementAudioSourceNode | null
  >(null);
  const silentGainRef = useRef<GainNode | null>(null);
  const rafRef = useRef<number | undefined>(undefined);
  const stopPrimaryAudioRef = useRef<(() => void) | null>(null);
  const playbackHandleRef = useRef<AudioPlaybackHandle | null>(null);

  const particlesRef = useRef<Particle[]>([]);
  const textBurstsRef = useRef<TextBurst[]>([]);
  const lastWordRef = useRef<number>(0);
  const effectStartedRef = useRef<boolean>(false);
  const canvasSizeRef = useRef<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });
  const hasCompletedRef = useRef<boolean>(false);
  const fadeOutStartTimeRef = useRef<number | null>(null);
  const FADE_OUT_DURATION_MS = 500;
  const ANIMATION_DURATION_MS = 5000;

  const cleanup = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = undefined;
    }
    if (analysisAudioRef.current) {
      analysisAudioRef.current.pause();
      analysisAudioRef.current.src = "";
      analysisAudioRef.current = null;
    }
    if (analyserSourceRef.current) {
      analyserSourceRef.current.disconnect();
      analyserSourceRef.current = null;
    }
    if (silentGainRef.current) {
      silentGainRef.current.disconnect();
      silentGainRef.current = null;
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.close();
      audioCtxRef.current = null;
    }
    if (stopPrimaryAudioRef.current) {
      stopPrimaryAudioRef.current();
      stopPrimaryAudioRef.current = null;
    }
  }, []);


  const startFadeOut = useCallback(() => {
    if (hasCompletedRef.current || fadeOutStartTimeRef.current !== null) {
      return;
    }
    fadeOutStartTimeRef.current = performance.now();
  }, []);

  const finishEffect = useCallback(() => {
    if (hasCompletedRef.current) {
      return;
    }
    hasCompletedRef.current = true;
    cleanup();
    onComplete();
  }, [cleanup, onComplete]);

  const startPrimaryAudio = useCallback(() => {
    if (!stopPrimaryAudioRef.current && playAudio) {
      const handle = playAudio();
      if (handle) {
        playbackHandleRef.current = handle;
        stopPrimaryAudioRef.current = () => handle.stop();
      }
    }
  }, [playAudio]);

  const setupAnalysisAudio = useCallback(async () => {
    try {
      if (typeof window === "undefined") {
        return false;
      }

      let audioCtx = audioCtxRef.current;
      if (!audioCtx) {
        const AudioContextCtor =
          window.AudioContext ||
          (window as unknown as { webkitAudioContext?: typeof AudioContext })
            .webkitAudioContext;
        if (!AudioContextCtor) {
          throw new Error("Web Audio API not available");
        }
        audioCtx = new AudioContextCtor();
        audioCtxRef.current = audioCtx;
      }

      if (audioCtx.state === "suspended") {
        await audioCtx.resume();
      }

      if (analyserRef.current) {
        return true;
      }

      startPrimaryAudio();
      const playbackAudio = playbackHandleRef.current?.audioElement;
      const setupFromElement = async (
        element: HTMLAudioElement
      ): Promise<boolean> => {
        const captureStream = (element as HTMLMediaElement & {
          captureStream?: () => MediaStream;
          mozCaptureStream?: () => MediaStream;
        }).captureStream?.();

        if (!captureStream && !(element as any).mozCaptureStream) {
          return false;
        }

        const stream =
          captureStream ||
          (element as HTMLMediaElement & {
            mozCaptureStream?: () => MediaStream;
          }).mozCaptureStream?.();
        if (!stream) {
          return false;
        }

        const source = audioCtx.createMediaStreamSource(stream);
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.85;
        const silentGain = audioCtx.createGain();
        silentGain.gain.value = 0;

        source.connect(analyser);
        analyser.connect(silentGain);
        silentGain.connect(audioCtx.destination);

        analyserSourceRef.current = source;
        silentGainRef.current = silentGain;
        analyserRef.current = analyser;
        freqDataRef.current = new Uint8Array(analyser.frequencyBinCount);
        return true;
      };

      if (playbackAudio) {
        const elementSetup = await setupFromElement(playbackAudio);
        if (elementSetup) {
          startPrimaryAudio();
          return true;
        }
      }

      const analysisAudio = new Audio(analysisTrackUrl);
      analysisAudio.crossOrigin = "anonymous";
      analysisAudio.preload = "auto";
      analysisAudio.muted = true;
      analysisAudioRef.current = analysisAudio;

      const source = audioCtx.createMediaElementSource(analysisAudio);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.85;

      const mutedGain = audioCtx.createGain();
      mutedGain.gain.value = 0;

      source.connect(analyser);
      analyser.connect(mutedGain);
      mutedGain.connect(audioCtx.destination);

      analyserSourceRef.current = source;
      silentGainRef.current = mutedGain;
      analyserRef.current = analyser;
      freqDataRef.current = new Uint8Array(analyser.frequencyBinCount);

      await analysisAudio.play();
      startPrimaryAudio();
      return true;
    } catch (error) {
      console.warn("[KitKitMiauMiau] Audio blocked:", error);
      // If audio fails, still start the animation with fallback audio levels
      return false;
    }
  }, [analysisTrackUrl, startPrimaryAudio]);

  const resizeCanvas = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const viewport = window.visualViewport;
    const width = Math.max(
      Math.floor(viewport?.width ?? window.innerWidth),
      1
    );
    const height = Math.max(
      Math.floor(viewport?.height ?? window.innerHeight),
      1
    );

    canvasSizeRef.current = { width, height };

    const setStyle = (prop: string, value: string) => {
      canvas.style.setProperty(prop, value, "important");
    };

    setStyle("position", "fixed");
    setStyle("top", "0");
    setStyle("left", "0");
    setStyle("right", "0");
    setStyle("bottom", "0");
    setStyle("width", `${width}px`);
    setStyle("height", `${height}px`);
    setStyle("max-width", `${width}px`);
    setStyle("max-height", `${height}px`);
    setStyle("min-width", `${width}px`);
    setStyle("min-height", `${height}px`);
    setStyle("margin", "0");
    setStyle("padding", "0");
    setStyle("background", "transparent");
    setStyle("background-color", "transparent");
    setStyle("backdrop-filter", "none");
    setStyle("-webkit-backdrop-filter", "none");
    setStyle("pointer-events", "none");
    setStyle("filter", "none");
    setStyle("border", "none");
    setStyle("box-shadow", "none");
    setStyle("z-index", "9999");
    setStyle("display", "block");
    setStyle("image-rendering", "crisp-edges");
    setStyle("will-change", "contents");
    setStyle("opacity", "1");
    setStyle("mix-blend-mode", "normal");
    setStyle("contain", "strict");

    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
    }
  }, []);

  const animate = useCallback(() => {
    if (hasCompletedRef.current) {
      return;
    }
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) {
      return;
    }

    // Calculate fade-out opacity
    let globalAlpha = 1;
    if (fadeOutStartTimeRef.current !== null) {
      const fadeElapsed = performance.now() - fadeOutStartTimeRef.current;
      if (fadeElapsed >= FADE_OUT_DURATION_MS) {
        finishEffect();
        return;
      }
      globalAlpha = 1 - fadeElapsed / FADE_OUT_DURATION_MS;
    }

    const dpr = window.devicePixelRatio || 1;
    const width =
      canvasSizeRef.current.width ||
      (canvas.width ? canvas.width / dpr : window.innerWidth);
    const height =
      canvasSizeRef.current.height ||
      (canvas.height ? canvas.height / dpr : window.innerHeight);
    
    ctx.save();
    ctx.globalAlpha = globalAlpha;
    ctx.clearRect(0, 0, width, height);

    // Draw "fragged by {name}" text at the top
    if (fraggerName) {
      ctx.save();
      ctx.fillStyle = "#FFFFFF";
      ctx.font = `600 ${Math.max(20, Math.min(width * 0.032, 40))}px "Impact", "Arial Black", sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.shadowColor = "rgba(0, 0, 0, 0.8)";
      ctx.shadowBlur = 8;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 2;
      const text = `fragged by ${fraggerName}`;
      const textY = Math.max(60, height * 0.12);
      ctx.fillText(text, width / 2, textY);
      ctx.restore();
    }

    const analyser = analyserRef.current;
    const freqData = freqDataRef.current;
    let audioLevel = 0.2;
    let bassLevel = 0.2;
    let highLevel = 0.2;

    if (analyser && freqData) {
      analyser.getByteFrequencyData(freqData as any);
      const splitIndex = Math.floor(freqData.length / 3);
      const bassNormalizer = Math.max(splitIndex * 255, 1);
      const highNormalizer = Math.max(
        (freqData.length - splitIndex) * 255,
        1
      );

      let totalSum = 0;
      let bassSum = 0;
      let highSum = 0;

      for (let i = 0; i < freqData.length; i++) {
        const value = freqData[i];
        totalSum += value;
        if (i < splitIndex) {
          bassSum += value;
        } else {
          highSum += value;
        }
      }

      audioLevel =
        totalSum / Math.max(freqData.length * 255, 1);
      bassLevel = bassSum / bassNormalizer;
      highLevel = highSum / highNormalizer;
    } else {
      const t = performance.now() * 0.001;
      audioLevel = (Math.sin(t * 1.5) + 1) / 2;
      bassLevel = (Math.sin(t * 0.9) + 1) / 2;
      highLevel = (Math.cos(t * 1.3) + 1) / 2;
    }

    const centerX = width / 2;
    const centerY = height / 2;
    const baseSize = Math.min(width, height) * 0.25;
    const rotation = performance.now() * 0.0008;

    drawCatOutline(
      ctx,
      centerX,
      centerY,
      baseSize * (1 + bassLevel * 0.4),
      `rgba(255,215,0,${clamp(0.4 + audioLevel * 0.5, 0.3, 1)})`,
      4 + audioLevel * 6,
      Math.sin(rotation) * 0.15
    );

    drawCatOutline(
      ctx,
      centerX,
      centerY,
      baseSize * 1.35 * (1 + highLevel * 0.2),
      `rgba(80,200,120,${clamp(0.25 + audioLevel * 0.4, 0.2, 0.8)})`,
      2,
      Math.cos(rotation) * 0.15
    );

    if (audioLevel > 0.05 && Math.random() > 0.5) {
      particlesRef.current.push(createParticle(width, height));
    }

    const now = performance.now();
    if (
      now - lastWordRef.current > 220 &&
      (highLevel > 0.2 || bassLevel > 0.33)
    ) {
      const text = highLevel > bassLevel ? TEXT_WORDS[1] : TEXT_WORDS[0];
      textBurstsRef.current.push(createTextBurst(width, height, text));
      lastWordRef.current = now;
    }

    particlesRef.current = particlesRef.current.filter((p) => p.life > 0);
    particlesRef.current.forEach((p) => {
      p.x += p.vx;
      p.y += p.vy;
      p.life -= 0.005;
      p.alpha = Math.sin(p.life * Math.PI);
      ctx.save();
      ctx.globalAlpha = clamp(p.alpha, 0, 1);
      drawCatOutline(ctx, p.x, p.y, p.size * 8, p.color, 1.4, p.vx * 0.2);
      ctx.restore();
    });

    textBurstsRef.current = textBurstsRef.current.filter((t) => t.life > 0);
    textBurstsRef.current.forEach((t) => {
      t.y -= 2;
      t.scale += 0.02;
      t.life -= 0.015;
      t.alpha = clamp(t.life / 0.7, 0, 1);
      ctx.save();
      ctx.globalAlpha = t.alpha;
      ctx.fillStyle = t.color;
      ctx.font = `900 ${30 * t.scale}px "Courier New", monospace`;
      ctx.textAlign = "center";
      ctx.shadowColor = t.color;
      ctx.shadowBlur = 12;
      ctx.fillText(t.text, t.x, t.y);
      ctx.restore();
    });

    ctx.restore();

    if (!hasCompletedRef.current) {
      rafRef.current = requestAnimationFrame(animate);
    }
  }, [finishEffect, fraggerName]);

  useEffect(() => {
    if (message.type !== "frag") {
      onComplete();
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) {
      onComplete();
      return;
    }

    const handleResize = () => {
      resizeCanvas();
    };

    resizeCanvas();
    window.addEventListener("resize", handleResize);
    const viewport = window.visualViewport;
    viewport?.addEventListener("resize", handleResize);
    viewport?.addEventListener("scroll", handleResize);

    // Start animation immediately
    effectStartedRef.current = true;
    rafRef.current = requestAnimationFrame(animate);

    // Setup audio analysis
    setupAnalysisAudio().catch((error) => {
      console.warn("[KitKitMiauMiau] Audio setup failed:", error);
    });

    // Start fade-out after 5 seconds
    window.setTimeout(() => {
      startFadeOut();
    }, ANIMATION_DURATION_MS);

    return () => {
      window.removeEventListener("resize", handleResize);
      const viewport = window.visualViewport;
      viewport?.removeEventListener("resize", handleResize);
      viewport?.removeEventListener("scroll", handleResize);
      cleanup();
    };
  }, [
    animate,
    cleanup,
    message.type,
    onComplete,
    resizeCanvas,
    setupAnalysisAudio,
    startFadeOut,
  ]);

  if (message.type !== "frag") {
    return null;
  }

  return (
    <>
      <style>{`
        .kitkit-overlay-canvas {
          position: fixed !important;
          inset: 0 !important;
          width: 100vw !important;
          height: 100vh !important;
          background: transparent !important;
          backdrop-filter: none !important;
          -webkit-backdrop-filter: none !important;
          border: none !important;
          box-shadow: none !important;
          filter: none !important;
          pointer-events: none !important;
          margin: 0 !important;
          padding: 0 !important;
          z-index: 9999 !important;
        }
      `}</style>
      <canvas
        ref={canvasRef}
        className="kitkit-overlay-canvas"
        style={{
          position: "fixed",
          inset: 0,
          width: "100vw",
          height: "100vh",
          background: "transparent",
          pointerEvents: "none",
          zIndex: 9999,
        }}
      />
    </>
  );
};

export default KitKitMiauMiauAnimation;

